import { MarkerModes, MarkerState } from '../../types'
import { BaseMarker } from './baseMarker'
import { BaseHandle } from './baseHandle'
import {
  MARKER_DEFAULT_STROKE_COLOR,
  MARKER_DEFAULT_STROKE_WIDTH,
  MARKER_HANDLE_DEFAULT_SIZE,
  MARKER_HANDLE_DEFAULT_STROKE_WIDTH,
  MARKER_DASH_LENGTH_DEFAULT,
  MARKER_DASH_SPACE_LENGTH_DEFAULT,
} from '../../constants'

export abstract class BaseMarkerStateManager<
  MarkerType extends BaseMarker<any, any, any, any>,
  StateType extends MarkerState,
  HandleType extends BaseHandle
> {
  public mode: MarkerModes
  protected _strokeWeight: number = MARKER_DEFAULT_STROKE_WIDTH
  protected _strokeColor: string = MARKER_DEFAULT_STROKE_COLOR //TODO replace with hex/rgb type
  protected _handleRadius: number = MARKER_HANDLE_DEFAULT_SIZE
  protected _handleStrokeweight: number = MARKER_HANDLE_DEFAULT_STROKE_WIDTH
  protected _dashLength = MARKER_DASH_LENGTH_DEFAULT
  protected _dashSpaceLength = MARKER_DASH_SPACE_LENGTH_DEFAULT
  protected _opacity = 100
  protected _alpha = 255
  public typeName: string
  public marker: MarkerType
  public mousedOverHandle: HandleType
  public isDashed = false
  public isSelected = false
  public isPressed = false
  public isHovered = false
  public isShowHandles = false
  public isSelectionLocked = false

  public handles: Array<HandleType> = []
  public pressedHandle: HandleType
  public _scalingRatio = 1
  public meta: { [key: string]: any }

  constructor(marker: MarkerType) {
    this.marker = marker
  }

  setToHoveredState() {
    this.isHovered = true
  }

  setToDehoveredState() {
    this.isHovered = false
  }

  /**
   * Updates state that puts marker into a selected and ready to edit rendering/interactivity mode
   */
  setToSelectedState() {
    this.mode = MarkerModes.Edit
    this.isSelected = true
    this.isShowHandles = true
  }

  /**
   * Updates state that puts marker into a deselected rendering mode and display rendering/interactivity mode
   */
  setToDeselectedState() {
    this.mode = MarkerModes.Display
    this.isSelected = false
    this.isShowHandles = false
    this.strokeColor = this.meta?.strokeCategoryColor || MARKER_DEFAULT_STROKE_COLOR
  }

  /**
   * Restores the markers state to the provided StateType definition
   * @param state Marker state object
   */
  public restore(state: StateType) {
    this.mode = state.mode
    this.strokeWeight = state?.strokeWeight || MARKER_DEFAULT_STROKE_WIDTH
    this.strokeColor = state?.strokeColor || MARKER_DEFAULT_STROKE_COLOR
    this.isDashed = state?.isDashed || false
    this.dashLength = state?.dashLength || MARKER_DASH_LENGTH_DEFAULT
    this.dashSpaceLength = state?.dashSpaceLength || MARKER_DASH_SPACE_LENGTH_DEFAULT
    this.meta = state?.meta || {}
    this.opacity = state?.opacity || 100
  }

  /**
   * Exports markers state to a serializable object
   * @returns MarkerState
   */
  public export(): StateType {
    return {
      typeName: this.typeName,
      mode: this.mode,
      strokeWeight: this.strokeWeight,
      strokeColor: this.strokeColor,
      opacity: this.opacity,
      meta: this.meta,
    } as StateType
  }

  /**
   * Get the markers stroke weight
   */
  get strokeWeight(): number {
    return this.scalingRatio !== 0 ? this._strokeWeight * this.scalingRatio : this._strokeWeight
  }

  /**
   * Set the markers stroke weight
   */
  set strokeWeight(weight: number) {
    this._strokeWeight = weight
  }

  /**
   * Get the markers stroke color
   */
  //TODO implement better return type - hex or rgb
  get strokeColor(): any {
    return this._strokeColor
  }

  /**
   * Set the markers stroke color
   */
  //TODO implement better type for color param - hex or rgb
  set strokeColor(color: any) {
    this._strokeColor = color
  }

  get scalingRatio(): number {
    return this._scalingRatio
  }

  set scalingRatio(newScalingRatio: number) {
    this._scalingRatio = newScalingRatio
  }

  get handleRadius(): number {
    return this.scalingRatio !== 0 ? this._handleRadius * this.scalingRatio : this._handleRadius
  }

  set handleRadius(newRadius) {
    this._handleRadius = newRadius
  }

  get handleStrokeWeight(): number {
    return this.scalingRatio !== 0 ? this._handleStrokeweight * this.scalingRatio : this._handleStrokeweight
  }

  set handleStrokeWeight(newStrokeWeight: number) {
    this._handleStrokeweight = newStrokeWeight
  }

  get dashLength(): number {
    return this.scalingRatio !== 0 ? this._dashLength * this.scalingRatio : this._dashLength
  }

  set dashLength(newDashLength: number) {
    this._dashLength = newDashLength
  }

  get dashSpaceLength(): number {
    return this.scalingRatio !== 0 ? this._dashSpaceLength * this.scalingRatio : this._dashSpaceLength
  }

  set dashSpaceLength(newDashSpaceLength: number) {
    this._dashSpaceLength = newDashSpaceLength
  }

  get opacity(): number {
    return this._opacity
  }

  set opacity(newOpacity: number) {
    this._opacity = newOpacity
    this._alpha = Math.round((newOpacity / 100) * 255)
  }

  get alpha(): number {
    return this._alpha
  }
}
